import React, { FormEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { authApi } from "../api/auth.api";
import { AuthApiError } from "../model/auth.types";
import { saveAuthSession } from "../model/auth.storage";
import { ROUTES } from "../../../system/router/routes";
import {
  AuthAlert,
  AuthCard,
  AuthLink,
  AuthLinkRow,
  AuthPasswordField,
  AuthSubmitButton,
  AuthTextField,
} from "./auth-kit";
import { AuthLayout } from "./auth-layout";
import * as Styled from "./auth.styled";

type RedirectState = {
  from?: {
    pathname?: string;
  };
  passwordResetSuccess?: boolean;
};

const getErrorMessage = (error: AuthApiError) => {
  if (error.code === "PASSWORD_EXPIRED") {
    return "Your password has expired and can no longer be used.";
  }

  return (
    error.message ||
    "Incorrect email and/or password. Please check and try again."
  );
};

export const SignInPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo =
    (location.state as RedirectState | null)?.from?.pathname || ROUTES.APP;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState("");
  const [apiError, setApiError] = useState("");
  const [createPasswordToken, setCreatePasswordToken] = useState("");
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(
    Boolean((location.state as RedirectState | null)?.passwordResetSuccess),
  );

  const signIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFieldError("");
    setApiError("");
    setCreatePasswordToken("");
    setPasswordResetSuccess(false);

    if (!email || !password) {
      setFieldError("Email and password are required.");
      return;
    }

    setLoading(true);

    try {
      const session = await authApi.signIn({ email, password, keepSignedIn });
      saveAuthSession(session, keepSignedIn);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      const authError = error as AuthApiError;
      setApiError(getErrorMessage(authError));

      if (authError.code === "PASSWORD_EXPIRED") {
        setCreatePasswordToken(authError.createPasswordToken || "");
      }
    } finally {
      setLoading(false);
    }
  };

  const alert = apiError ? (
    <AuthAlert
      onClose={() => {
        setApiError("");
        setCreatePasswordToken("");
      }}
    >
      {apiError}
      {createPasswordToken ? (
        <>
          {" "}
          Please{" "}
          <Styled.AlertAction
            as={AuthLink}
            to={`${ROUTES.CREATE_PASSWORD}?token=${createPasswordToken}&email=${encodeURIComponent(email)}`}
          >
            reset your password
          </Styled.AlertAction>{" "}
          to continue.
        </>
      ) : null}
    </AuthAlert>
  ) : passwordResetSuccess ? (
    <AuthAlert tone="success" onClose={() => setPasswordResetSuccess(false)}>
      All set! Your password has been successfully reset.
    </AuthAlert>
  ) : undefined;

  return (
    <AuthLayout alert={alert} demo>
      <AuthCard title="Sign in">
        <Styled.Form onSubmit={signIn} noValidate>
          <AuthTextField
            id="email"
            label="Email"
            name="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
          />

          <AuthPasswordField
            id="password"
            label="Password"
            name="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
          />

          {fieldError ? (
            <Styled.FieldError>{fieldError}</Styled.FieldError>
          ) : null}

          <Styled.CheckboxLabel>
            <input
              type="checkbox"
              checked={keepSignedIn}
              onChange={(event) => setKeepSignedIn(event.target.checked)}
            />
            Keep me signed in
          </Styled.CheckboxLabel>

          <AuthSubmitButton loading={loading} loadingLabel="Signing in...">
            Continue
          </AuthSubmitButton>

          <AuthLinkRow>
            <AuthLink to={ROUTES.FORGOT_PASSWORD}>
              Forgot your password?
            </AuthLink>
          </AuthLinkRow>
        </Styled.Form>
      </AuthCard>
    </AuthLayout>
  );
};
